import { EventEmitter } from 'events';
import * as Y from 'yjs';

import { SCHEMAS } from '@gitbook/doc-core';
import {
    checkYDocument,
    dataSyncProtocol,
    documentChangeToY,
    getYSlateDocument,
    normalizeYDocument,
    selectionToYSelection,
    YDocumentCheckFail,
    ySelectionToSelection,
    yToDocument,
} from '@gitbook/hive-core';
import {
    BlockRange,
    type Change,
    Data,
    type Document,
    deserializeFromProtocolBuffer,
    type SelectionRange,
    serialize,
    serializeToProtocolBuffer,
    TextRange,
    Value,
} from '@gitbook/slate';
import { ReportException, reportException, SentryTags, startStopwatch } from '@gitbook/util-debug';
import { List } from '@gitbook/util-immutable';
import { filterOutNullable } from '@gitbook/util-ts';

import type { HiveDataSyncBrowserCache } from './HiveDataSyncBrowserCache';
import {
    HiveDataSyncClient,
    HiveDataSyncState,
    HiveDataSyncUpdateOrigin,
    type HiveGlobalPresence,
} from './HiveDataSyncClient';
import type { HiveDataSyncManager } from './HiveDataSyncManager';

export type HiveDocumentPresence = HiveGlobalPresence;

export interface HiveDocumentSelectionData {
    clientId: string;
}

export interface HiveWorkingDocumentOptions {
    /**
     * When user is editing alone, we batch updates to avoid sending too many messages
     * to the server.
     * When editing with other users, we don't batch to ensure a perception of realtime collaboration.
     *
     * Wait time before sending update to the server when editing alone.
     * @default 1500 ms
     */
    singleEditBatchingInterval?: number;

    /**
     * Is the datasync client opened in read-only mode
     * @default false
     */
    readOnly?: boolean;

    /**
     * A cache to use with the datasync.
     */
    cache?: HiveDataSyncBrowserCache;

    /** The initial Y.Doc - only use for testing. */
    dangerouslySetInitialDoc?: Y.Doc;
}

export type HiveDocumentEditSuggestion<TData extends Record<string, unknown> = {}> = {
    /** ID of the suggestion */
    id: string;
    /** User ID of the author of the suggestion */
    author: string;
    /** Range to be replaced in the document */
    range: SelectionRange;
    /** Metadata associated with the suggestion */
    data: TData;
    /** Content suggested to replace the range */
    content: Document;
};

/**
 * Preserve transient local selection state when rebuilding a selection after a collaborative document update.
 */
export function preserveSelectionStateAfterDocumentUpdate(
    selection: SelectionRange,
    previousSelection: SelectionRange
): SelectionRange {
    if (
        TextRange.isTextRange(selection) &&
        TextRange.isTextRange(previousSelection) &&
        previousSelection.isFocused
    ) {
        return selection.merge({
            marks: previousSelection.marks,
            data: previousSelection.data,
        });
    }

    return selection;
}

/**
 * Return a unique identifier for a Hive working branch.
 */
export function getHiveWorkingDocumentGuid({
    contentId,
    branchId,
    containerType,
    containerId,
}: {
    contentId: string;
    branchId: string;
    containerType: 'pages' | 'reusable-contents';
    containerId: string;
}) {
    return `content/${contentId}/branches/${branchId}/${containerType}/${containerId}/document/datasync`;
}

/**
 * Sync a Slate document with a Hive server.
 */
export class HiveWorkingDocument {
    public readonly contentId: string;
    public readonly branchId: string;
    public readonly containerType: 'pages' | 'reusable-contents';
    public readonly containerId: string;

    public selections: List<SelectionRange> = List.create();
    public editSuggestions: List<HiveDocumentEditSuggestion> = List.create();

    private readonly manager: HiveDataSyncManager;
    public readonly dataSync: HiveDataSyncClient<HiveDocumentPresence>;

    private events = new EventEmitter();
    private singleEditBatchingInterval: number;

    #value: Value;

    constructor(
        manager: HiveDataSyncManager,
        guid: string,
        params: {
            contentId: string;
            branchId: string;
            containerType: 'pages' | 'reusable-contents';
            containerId: string;
        },
        options: HiveWorkingDocumentOptions = {}
    ) {
        this.manager = manager;
        this.contentId = params.contentId;
        this.branchId = params.branchId;
        this.containerType = params.containerType;
        this.containerId = params.containerId;

        this.singleEditBatchingInterval =
            options.singleEditBatchingInterval ?? (process.env.NODE_ENV === 'test' ? 0 : 1500);

        this.#value = Value.create();

        this.dataSync = new HiveDataSyncClient(manager.client, {
            guid,
            contentId: this.contentId,
            path: '/branches/:branchId/:containerType/:containerId/document/datasync',
            pathParams: {
                branchId: this.branchId,
                containerType: this.containerType,
                containerId: this.containerId,
            },
            YType: Y.XmlElement,
            readOnly: options.readOnly,
            cache: options.cache,
            dangerouslySetInitialDoc: options.dangerouslySetInitialDoc,
        });
        this.dataSync.resetBatchingInterval(this.singleEditBatchingInterval);

        this.dataSync.listen('restorePositions', (positions) => {
            let selection = this.createSelectionFromRelatives(this.value.document, positions);
            selection = selection ? selection.normalize(this.value.document) : TextRange.create();

            if (this.#value.selection.isFocused) {
                selection = selection.focus();
            }
            this.#value = this.#value.merge({
                selection,
            });
            this.events.emit('value');
        });

        this.dataSync.listen('update', (origin: string) => {
            if (origin === HiveDataSyncUpdateOrigin.LOCAL) {
                // .doc and .value should be in sync already
            } else {
                this.onDataSyncUpdate();
            }

            this.events.emit('value');
        });

        this.dataSync.listen('presences', () => {
            if (this.dataSync.presences.length > 0) {
                if (this.dataSync.hasBatchingEnabled()) {
                    this.dataSync.disableBatching();
                }
            } else {
                if (!this.dataSync.hasBatchingEnabled()) {
                    this.dataSync.resetBatchingInterval(this.singleEditBatchingInterval);
                }
            }

            this.updateSelections();
        });

        this.dataSync.listen('markers', () => {
            this.updateEditSuggestions();
        });

        this.dataSync.listen('state', (state) => {
            if (state === HiveDataSyncState.OPEN) {
                this.updateSelections();
            }
        });
    }

    get guid() {
        return getHiveWorkingDocumentGuid({
            contentId: this.contentId,
            branchId: this.branchId,
            containerType: this.containerType,
            containerId: this.containerId,
        });
    }

    /**
     * Getter for the current value of the document.
     * It makes sure `this.#value` can't be modified outside of the class.
     */
    get value() {
        return this.#value;
    }

    /**
     * Convert the Yjs document into a complete Slate value.
     */
    private onDataSyncUpdate() {
        const yDocument = getYSlateDocument(this.dataSync.doc);

        if (this.dataSync.needsClientSideNormalization) {
            this.dataSync.doc.transact(() => {
                return normalizeYDocument(yDocument);
            }, HiveDataSyncUpdateOrigin.LOCAL);
        }

        const document = yToDocument(yDocument);
        const previousDocument = this.value.document;
        const previousSelection = this.value.selection;
        const schema =
            SCHEMAS[(document.data.get('schemaType') || 'document') as keyof typeof SCHEMAS];

        let value = Value.create(
            {
                document,
            },
            {
                normalize: false,
            }
        ).setSchema(schema);

        if (value.document !== document) {
            // We run this check to absolutely make sure that we manage any form of normalization
            // below to sync with the other clients and not create a mismatch between `value` and `this.dataSync.doc`.
            throw new Error('Document should not be modified by creation of Value');
        }

        const updateSelectionsAndValue = () => {
            const { selection, reanchorPresence } = this.getSelectionAfterDocumentUpdate(
                previousDocument,
                value.document,
                previousSelection
            );
            value = value.merge({ selection });

            // Set the value before finishing the transaction. It means anything listening to
            // updates on the doc will see the new value.
            this.#value = value;

            return reanchorPresence;
        };

        // Perform client-side normalization if needed
        let reanchorPresence: boolean;
        if (this.dataSync.needsClientSideNormalization) {
            reanchorPresence = this.dataSync.doc.transact(() => {
                // Normalize the document and sync it with the server if it changed
                const normalization = value.change({ save: false }).normalize();
                if (normalization.value.document !== value.document) {
                    value = value.merge({
                        document: normalization.value.document,
                    });

                    documentChangeToY(normalization, yDocument);
                    const getExecutionTime = startStopwatch();

                    try {
                        // We've seen issues where, after documentChangeToY, the Yjs doc does not match the passed Slate doc.
                        checkYDocument(normalization.value.document, yDocument);
                    } catch (error) {
                        if (!(error instanceof YDocumentCheckFail)) {
                            throw error;
                        }

                        const durationMs = getExecutionTime();

                        // It'll crash the app but it prevents potentially invalid Yjs documents from being synced to the server.
                        this.dataSync.destroy();

                        // Report the error in Sentry along with the operations in the change.
                        throw ReportException.annotateError(error, {
                            tags: {
                                [SentryTags.ERROR_CATEGORY]: 'hive-document-init',
                            },
                            extra: {
                                durationMs,
                                contentId: this.contentId,
                                branchId: this.branchId,
                                containerType: this.containerType,
                                containerId: this.containerId,
                            },
                            attachments: [
                                {
                                    name: 'normalization.json',
                                    body: serializeChange(error, normalization),
                                },
                                {
                                    name: 'updates.bin',
                                    body: this.dataSync.getUpdateLog(),
                                },
                            ],
                        });
                    }
                }

                return updateSelectionsAndValue();
            }, HiveDataSyncUpdateOrigin.LOCAL);
        } else {
            reanchorPresence = updateSelectionsAndValue();
        }

        if (reanchorPresence) {
            this.setPresenceSelection(value.document, value.selection);
        }
    }

    /**
     * Compute the local selection once the document changed under it. The editor owns its
     * selection: it's only moved as far as needed to stay valid, and never unset or blurred.
     */
    private getSelectionAfterDocumentUpdate(
        previousDocument: Document,
        document: Document,
        previousSelection: SelectionRange
    ): { selection: SelectionRange; reanchorPresence: boolean } {
        // The Yjs nodes the presence pointed at were replaced: a split, merge, move or delete
        // deletes and re-inserts whole nodes.
        const mapThroughText = (range: TextRange) => ({
            selection: restoreLocalSelectionState(
                mapTextRangeThroughText(previousDocument, document, range),
                previousSelection,
                document
            ),
            reanchorPresence: true,
        });

        if (previousSelection.isUnset) {
            return { selection: previousSelection, reanchorPresence: false };
        }

        if (BlockRange.isBlockRange(previousSelection)) {
            // Blocks are selected by key, whatever happens to their content.
            const selection = previousSelection.normalize(document);
            return selection.isSet
                ? { selection, reanchorPresence: false }
                : mapThroughText(TextRange.fromBlockRange(previousSelection, previousDocument));
        }

        const relativeSelection = this.dataSync.presence
            ? this.createSelectionFromRelatives(document, this.dataSync.presence.positions)
            : undefined;

        if (
            isTextUnchanged(previousDocument, document, previousSelection.anchorKey) &&
            isTextUnchanged(previousDocument, document, previousSelection.focusKey)
        ) {
            // The offsets are still valid, but a moved block can reverse a selection across
            // texts. The presence may still point at Yjs nodes that were replaced by
            // identical ones (e.g. a moved block).
            const selection =
                previousSelection.anchorKey === previousSelection.focusKey
                    ? previousSelection
                    : previousSelection.merge({ isBackward: null }).normalize(document);
            return { selection, reanchorPresence: !relativeSelection };
        }

        if (relativeSelection) {
            return {
                selection: restoreLocalSelectionState(
                    relativeSelection,
                    previousSelection,
                    document
                ),
                reanchorPresence: false,
            };
        }

        return mapThroughText(previousSelection);
    }

    /**
     * Prevent the manager from closing this sync before the cleanup callback is called.
     */
    public keepAlive() {
        return this.manager.keepAlive(this);
    }

    /**
     * Connect to the backend of the document.
     */
    public open(): void | Promise<void> {
        return this.dataSync.open(
            this.manager.clientId
                ? {
                      clientId: this.manager.clientId,
                  }
                : {}
        );
    }

    /**
     * Close the connection to the branch and garbage collect it.
     */
    public close() {
        this.manager.close(this);
    }

    /**
     * Destroy the connection. It cannot be re-opened later and needs to be recreated.
     */
    public destroy() {
        this.dataSync.destroy();
    }

    /**
     * Wait for all changes to be synced.
     */
    public waitForSync() {
        return this.dataSync.waitForSync();
    }

    /**
     * Add a callback to be called on certain events.
     *  - 'change': the document has changed
     *  - 'selections': the selections have changed
     */
    public listen(
        event: 'value' | 'selections' | 'editSuggestions',
        callback: () => void
    ): () => void {
        this.events.addListener(event, callback);

        return () => {
            this.events.removeListener(event, callback);
        };
    }

    /**
     * Apply a change to the synced value.
     *
     * Called from the editor's `onChange`, where a throw crashes the editor, so a read-only sync
     * reports the refusal instead. `change()` still throws.
     */
    public applyChange(change: Change): void {
        // A merge can lock the sync before the editor re-renders as read-only. Keep only a caret
        // move built from the current value (selection isn't synced); drop the rest and resync.
        if (
            this.dataSync.clientReadOnly &&
            (change.value.document !== change.initialValue.document ||
                change.initialValue !== this.value)
        ) {
            reportException(new Error(`can't apply change when dataSync is in read-only mode`), {
                level: 'warning',
                tags: {
                    [SentryTags.ERROR_CATEGORY]: 'hive-document-read-only',
                },
                extra: {
                    contentId: this.contentId,
                    branchId: this.branchId,
                    containerType: this.containerType,
                    containerId: this.containerId,
                },
            });

            this.onDataSyncUpdate();
            this.events.emit('value');

            return;
        }

        this.manager.touch(this);

        const previousValue = this.value;

        if (previousValue !== change.initialValue) {
            const error = new YDocumentCheckFail(
                'Slate change.initialValue does not match the value of HiveWorkingDocument. This could indicate a nested .change() call, or a datasync inconsistency issue.'
            );

            // Destroy the datasync and crash.
            this.dataSync.destroy();

            // The Change was not created with the correct Yjs doc.
            // Report the error in Sentry along with the operations in the change.
            throw ReportException.annotateError(error, {
                tags: {
                    [SentryTags.ERROR_CATEGORY]: 'hive-document-initial-value',
                },
                extra: {
                    contentId: this.contentId,
                    branchId: this.branchId,
                    containerType: this.containerType,
                    containerId: this.containerId,
                },
                attachments: [
                    {
                        name: 'change.json',
                        body: serializeChange(error, change),
                    },
                    {
                        name: 'updates.bin',
                        body: this.dataSync.getUpdateLog(),
                    },
                ],
            });
        }

        this.#value = change.value;

        if (previousValue.document !== change.value.document) {
            const getExecutionTime = startStopwatch();

            this.dataSync.transact((doc) => {
                // Modify the Yjs doc using the new slate value.
                documentChangeToY(change, getYSlateDocument(doc));

                try {
                    // We've seen issues where, after documentChangeToY, the Yjs doc does not match the passed Slate doc.
                    // e.g. https://linear.app/gitbook-x/issue/SUP-737/git-sync-export-fail-cannot-read-properties-of-undefined
                    // We are trying to find the root cause by checking the Yjs doc against the Slate doc and reporting if they don't match.
                    checkYDocument(change.value.document, getYSlateDocument(doc));
                } catch (error) {
                    if (!(error instanceof YDocumentCheckFail)) {
                        throw error;
                    }

                    const durationMs = getExecutionTime();

                    // Revert the value and stop everything
                    this.#value = previousValue;
                    this.dataSync.destroy();

                    // Report the error in Sentry along with the operations in the change.
                    throw ReportException.annotateError(error, {
                        tags: {
                            [SentryTags.ERROR_CATEGORY]: 'hive-document-change',
                        },
                        extra: {
                            durationMs,
                            contentId: this.contentId,
                            branchId: this.branchId,
                            containerType: this.containerType,
                            containerId: this.containerId,
                        },
                        attachments: [
                            {
                                name: 'change.json',
                                body: serializeChange(error, change),
                            },
                            {
                                name: 'updates.bin',
                                body: this.dataSync.getUpdateLog(),
                            },
                        ],
                    });
                }
            });

            // When document is changed locally, we also update the selections to best position them
            // in the new document.
            this.updateSelections();
        } else {
            // Emit the event to indicate that the value changed
            // as it'll not be emitted by the `update` event of the data sync client.
            this.events.emit('value');
        }

        if (change.value.selection !== previousValue.selection) {
            this.setPresenceSelection(change.value.document, change.value.selection);
        }

        if (change.value.selection.isBlurred) {
            // When user is blurring the editor, we flush all updates to the server
            // in case they are blurring it to trigger a side effect that might depend on the updates
            // (e.g. merging the branch).
            void this.dataSync.flushBatchUpdates();
        }
    }

    /**
     * Make a change to the synced value.
     */
    public async change(callback: (change: Change) => void): Promise<void> {
        if (this.dataSync.clientReadOnly) {
            throw new Error(`can't apply change when dataSync is in read-only mode`);
        }

        const change = this.value.change();
        callback(change);
        this.applyChange(change);

        await this.waitForSync();
    }

    /**
     * Share the local selection with the other clients.
     */
    private setPresenceSelection(document: Document, selection: SelectionRange) {
        this.dataSync.setPresence({
            data: { ...this.dataSync.presence?.data },
            positions: selectionToYSelection(document, this.dataSync.doc, selection),
        });
    }

    /**
     * Update the list of selections from the map of presences.
     */
    private updateSelections() {
        if (this.dataSync.state !== HiveDataSyncState.OPEN) {
            // Don't update selections if we're not connected
            // as this.dataSync.doc might not be available as a representation of the document.
            this.selections = List.create();
        } else {
            this.selections = List.create(
                this.dataSync.presences
                    .map((presence) => {
                        const selection = this.createSelectionFromRelatives(
                            this.value.document,
                            presence.positions
                        );
                        if (!selection) {
                            return null;
                        }

                        return selection.merge({
                            data: Data.create({
                                clientId: presence.clientId,
                                ...presence.data,
                            }),
                        });
                    })
                    .filter(filterOutNullable)
            );
        }

        this.events.emit('selections');
    }

    /**
     * Update the list of edit suggestions from the content markers.
     */
    private updateEditSuggestions() {
        this.editSuggestions = List.create(
            this.dataSync.markers
                .map((marker) => {
                    if (marker.type !== dataSyncProtocol.ContentMarkerType.SUGGESTION) {
                        return null;
                    }

                    const range = this.createSelectionFromRelatives(this.value.document, {
                        anchor: marker.anchor,
                        focus: marker.focus,
                    });

                    if (!range || !marker.content) {
                        return null;
                    }

                    const documentSuggestion: HiveDocumentEditSuggestion = {
                        id: marker.id,
                        range,
                        author: marker.author,
                        data: marker.data,
                        content: deserializeFromProtocolBuffer(marker.content),
                    };

                    return documentSuggestion;
                })
                .filter(filterOutNullable)
        );

        this.events.emit('editSuggestions');
    }

    /**
     * Undo the last change.
     */
    public undo() {
        return this.dataSync.undo();
    }

    /**
     * Redo the last undoed change.
     */
    public redo() {
        return this.dataSync.redo();
    }

    /**
     * Create an edit suggestion.
     */
    public async createEditSuggestion<TData extends Record<string, unknown> = {}>(params: {
        /** Range to suggest replacing */
        range: SelectionRange;
        /** Data associated with the edit suggestion */
        data: TData;
        /** Content suggested to replace the range */
        content: Document;
    }): Promise<string> {
        const { anchor, focus } = selectionToYSelection(
            this.value.document,
            this.dataSync.doc,
            params.range
        );

        if (!anchor || !focus) {
            throw new Error('Cannot create edit suggestion without a valid range');
        }

        const contentBuffer = serializeToProtocolBuffer(params.content);

        const suggestionId = await this.dataSync.createMarker({
            type: dataSyncProtocol.ContentMarkerType.SUGGESTION,
            content: contentBuffer,
            data: params.data,
            anchor: Y.createRelativePositionFromTypeIndex(anchor.type, anchor.index, anchor.assoc),
            focus: Y.createRelativePositionFromTypeIndex(focus.type, focus.index, focus.assoc),
        });

        return suggestionId;
    }

    /**
     * Apply an edit suggestion, replacing the suggested range with the suggested content.
     */
    public async applyEditSuggestion(suggestionId: string): Promise<void> {
        const suggestion = this.getEditSuggestionById(suggestionId);
        if (!suggestion) {
            throw new Error(`Edit suggestion not found: ${suggestionId}`);
        }

        await this.change((change) => {
            change.insertDocumentAtRange(
                BlockRange.isBlockRange(suggestion.range)
                    ? TextRange.fromBlockRange(suggestion.range, change.value.document)
                    : suggestion.range,
                suggestion.content
            );
        });

        await this.dataSync.removeMarker(suggestionId);
    }

    /**
     * Reject an edit suggestion.
     */
    public async rejectEditSuggestion(suggestionId: string): Promise<void> {
        const suggestion = this.getEditSuggestionById(suggestionId);
        if (!suggestion) {
            throw new Error(`Edit suggestion not found: ${suggestionId}`);
        }

        await this.dataSync.removeMarker(suggestionId);
    }

    /**
     * Get an edit suggestion by its ID.
     */
    public getEditSuggestionById(suggestionId: string): HiveDocumentEditSuggestion | undefined {
        return this.editSuggestions.find((suggestion) => suggestion.id === suggestionId);
    }

    /**
     * Create a Slate selection from a selection composed of Yjs relative positions.
     */
    private createSelectionFromRelatives(
        document: Document,
        position: Record<string, Y.RelativePosition>
    ): SelectionRange | undefined {
        const doc = this.dataSync.doc;

        return ySelectionToSelection(document, {
            anchor: position.anchor
                ? Y.createAbsolutePositionFromRelativePosition(position.anchor, doc) || undefined
                : undefined,
            focus: position.focus
                ? Y.createAbsolutePositionFromRelativePosition(position.focus, doc) || undefined
                : undefined,
        });
    }
}

/**
 * Whether a text node exists in both documents with the same content.
 */
function isTextUnchanged(previousDocument: Document, document: Document, key: string | null) {
    if (!key) {
        return false;
    }

    const previousNode = previousDocument.getDescendant(key);
    const node = document.getDescendant(key);
    return Boolean(previousNode && node && previousNode.text === node.text);
}

/**
 * Normalize a selection moved by a document update, keeping the local state of the previous one.
 */
function restoreLocalSelectionState(
    selection: SelectionRange,
    previousSelection: SelectionRange,
    document: Document
): SelectionRange {
    const restored = preserveSelectionStateAfterDocumentUpdate(
        selection.normalize(document),
        previousSelection
    );
    return previousSelection.isFocused ? restored.focus() : restored;
}

/**
 * Map a text range to an updated document by diffing the text of both documents. Splits and
 * merges keep the text, so they map exactly; a point in replaced text moves to where it started.
 */
function mapTextRangeThroughText(
    previousDocument: Document,
    document: Document,
    range: TextRange
): TextRange {
    const previousTexts = previousDocument.getTextsAsArray(true);
    const texts = document.getTextsAsArray(true);
    if (texts.length === 0) {
        return TextRange.create();
    }

    const previousContent = previousTexts.map((text) => text.text).join('');
    const content = texts.map((text) => text.text).join('');

    const maxLength = Math.min(previousContent.length, content.length);
    let prefix = 0;
    while (prefix < maxLength && previousContent[prefix] === content[prefix]) {
        prefix++;
    }
    let suffix = 0;
    while (
        suffix < maxLength - prefix &&
        previousContent[previousContent.length - 1 - suffix] ===
            content[content.length - 1 - suffix]
    ) {
        suffix++;
    }

    const mapPoint = (key: string | null, offset: number) => {
        const index = previousTexts.findIndex((text) => text.key === key);
        let position =
            index === -1
                ? 0
                : previousTexts.slice(0, index).reduce((sum, text) => sum + text.text.length, 0) +
                  offset;

        if (position > prefix) {
            position =
                position >= previousContent.length - suffix
                    ? position + content.length - previousContent.length
                    : prefix;
        }

        // A position between two texts belongs to both: stay in the same text when it still
        // exists, otherwise take the earlier one.
        let point: { key: string; offset: number } | undefined;
        let start = 0;
        for (const text of texts) {
            const end = start + text.text.length;
            if (position >= start && position <= end) {
                if (text.key === key) {
                    return { key: text.key, offset: position - start };
                }
                point ??= { key: text.key, offset: position - start };
            }
            start = end;
        }

        return point ?? { key: texts[texts.length - 1].key, offset: start };
    };

    const anchor = mapPoint(range.anchorKey, range.anchorOffset);
    const focus = range.isCollapsed ? anchor : mapPoint(range.focusKey, range.focusOffset);
    return TextRange.create({
        anchorKey: anchor.key,
        anchorOffset: anchor.offset,
        focusKey: focus.key,
        focusOffset: focus.offset,
    });
}

/**
 * Serialize an error-causing Change to be sent to Sentry.
 */
function serializeChange(error: YDocumentCheckFail, change: Change): string {
    return JSON.stringify({
        initialValue: serialize(change.initialValue),
        ...(error.slateValue ? { slateValue: error.slateValue } : {}),
        ...(error.yValue ? { yValue: error.yValue } : {}),
        operations: change.operations.map(([operation]) => serialize(operation)),
        flags: Object.keys(change.flags).length > 0 ? change.flags : undefined,
    });
}
